import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/work", label: "Work" },
  { to: "/contact", label: "Contact" },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#050505]/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 group-hover:scale-125 transition-transform" />
            <span className="font-display font-extrabold text-lg tracking-tight text-white">
              BLUEVERA
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    isActive ? "text-cyan-400" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" data-testid="nav-avatar" className="flex items-center gap-2 group">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-9 h-9 rounded-full border border-white/15 group-hover:border-cyan-400 transition-colors" />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-cyan-500/10 grid place-items-center text-cyan-400 font-semibold text-sm">{user.name?.[0] || "U"}</span>
                )}
                <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">{user.name?.split(" ")[0]}</span>
              </Link>
            ) : (
              <button
                onClick={startGoogleLogin}
                data-testid="nav-signin"
                className="text-sm font-medium text-zinc-300 hover:text-white transition-colors px-3 py-2"
              >
                Sign in
              </button>
            )}
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="inline-flex items-center gap-1.5 bg-cyan-500 text-black font-semibold text-sm pl-5 pr-4 py-2.5 rounded-full hover:bg-cyan-400 hover:scale-105 transition-transform"
            >
              Start a Project <ArrowUpRight size={16} />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-0 z-40 bg-[#050505] pt-24 px-6 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="font-display text-4xl font-bold py-3 border-b border-white/10 text-white"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" className="mt-6 inline-flex items-center justify-center gap-2 bg-cyan-500 text-black font-semibold px-6 py-4 rounded-full">
                Start a Project <ArrowUpRight size={18} />
              </Link>
              {user ? (
                <Link to="/dashboard" className="mt-2 inline-flex items-center justify-center gap-2 border border-white/15 text-white font-semibold px-6 py-4 rounded-full">
                  My Dashboard
                </Link>
              ) : (
                <button onClick={startGoogleLogin} className="mt-2 inline-flex items-center justify-center gap-2 border border-white/15 text-white font-semibold px-6 py-4 rounded-full">
                  Sign in with Google
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
